import React from "react";
import PageLayout from "@/components/layout/PageLayout";
import { Hero, Section, SectionHeader, Bento } from "@/components/layout/Sections";
import BookingForm from "@/components/BookingForm";
import { Zap, Cog, Snowflake, Cpu, CircleDot, Truck, Anchor, Fuel, Wrench, ShieldCheck, Home, Car } from "lucide-react";

export default function AutoOfficina() {
  return (
    <PageLayout>
      <Hero
        image="/showroom/auto-officina.jpg"
        eyebrow="Auto · Officina"
        title="La nostra officina: l'eccellenza nei servizi di manutenzione e riparazione"
        subtitle="Assistenza a 360° per auto private e veicoli aziendali."
      />

      <Section>
        <div className="grid md:grid-cols-5 gap-10 md:gap-16">
          <div className="md:col-span-3">
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Il fiore all'occhiello</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
              L'officina specializzata al servizio del cliente
            </h2>
          </div>
          <div className="md:col-span-2 text-gray-700 leading-relaxed">
            <p>
              La nostra officina può intervenire su <strong>auto private e aziendali multibrand</strong> e su furgoni e veicoli commerciali,
              acquistati sia presso il nostro autosalone che presso altri rivenditori.
            </p>
          </div>
        </div>
      </Section>

      <Section tone="gray">
        <SectionHeader
          eyebrow="Metodo di lavoro"
          title="Piccoli dettagli che fanno la differenza"
        />
        <Bento
          items={[
            { eyebrow: "Software BOSCH", title: "Registrazione tagliandi", body: "Utilizziamo un software BOSCH dedicato che consente la registrazione dell'intervento direttamente nel portale di casa madre.", span: "md:col-span-3", tone: "dark" },
            { eyebrow: "Ricambi", title: "Originali e conformi", body: "Ricambi originali e/o conformi, per garantire un lavoro 'a regola d'arte' e la conservazione della garanzia di casa madre.", span: "md:col-span-3" },
            { eyebrow: "Diagnosi", title: "Come in concessionaria", body: "Diagnostica con lo strumento di casa madre di ogni marca, per una diagnosi ancora più precisa.", span: "md:col-span-6" },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader
          eyebrow="Servizi completi"
          title="Non una semplice officina"
          description="Dal semplice tagliando alla revisione dei cambi automatici, un servizio di riparazione completo."
        />
        <div className="grid md:grid-cols-2 gap-6">
          <Card icon={<Zap size={22} />} title="Veicoli elettrici e ibridi" body="Riparatori abilitati con qualifica PEI in conformità alle norme CEI 11-27 e CEI/EN 50110-2." />
          <Card icon={<Cog size={22} />} title="Cambi automatici" body="Specializzati nella manutenzione e revisione dei cambi automatici." />
          <Card icon={<Snowflake size={22} />} title="Climatizzazione" body="Diagnosi e ricarica dei sistemi di climatizzazione, sia gas R134 che R1234yf." />
          <Card icon={<Cpu size={22} />} title="Meccatronica" body="La nostra forza: qui si concentrano oggi la maggior parte dei problemi delle automobili moderne." />
          <Card icon={<CircleDot size={22} />} title="Gommista CONTINENTAL" body="Rivenditori ufficiali Continental e collaborazioni con partner e brand diversi." />
          <Card icon={<Truck size={22} />} title="Flotte aziendali" body="Gestiamo alcune delle più importanti flotte aziendali come Volkswagen Leasing, Mercury e altri." />
          <Card icon={<Anchor size={22} />} title="Ganci traino" body="Installatori autorizzati di ganci traino su auto e veicoli commerciali." />
          <Card icon={<Fuel size={22} />} title="Impianti GPL" body="Installatori autorizzati di impianti GPL con collaudo e assistenza." />
        </div>
      </Section>

      <Section tone="gray">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center justify-center h-12 w-12 mb-4 bg-white text-[color:var(--nsa-orange)] border border-gray-200">
              <ShieldCheck size={22} />
            </div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Consulenza usato</span>
            <h3 className="font-display uppercase text-3xl md:text-4xl mt-3">Un usato in ottime condizioni</h3>
            <p className="mt-4 text-gray-700 leading-relaxed">
              Presso la nostra officina verifichiamo lo stato delle auto usate in vendita o conto vendita nel nostro autosalone.
              Analizziamo componentistica meccanica ed elettronica, carrozzeria, usura di gomme e freni, e se necessario ci occupiamo
              delle riparazioni prima di mettere i veicoli sul mercato — così da garantirti auto e furgoni usati sempre in ottime condizioni.
            </p>
          </div>
          <div>
            <div className="flex items-center justify-center h-12 w-12 mb-4 bg-white text-[color:var(--nsa-orange)] border border-gray-200">
              <Home size={22} />
            </div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Servizio innovativo</span>
            <h3 className="font-display uppercase text-3xl md:text-4xl mt-3">Servizio a domicilio e auto sostitutiva</h3>
            <p className="mt-4 text-gray-700 leading-relaxed">
              Il tuo tempo è prezioso, soprattutto in ambito lavorativo. Per questo veniamo presso la tua sede, ritiriamo l'auto o
              il veicolo aziendale da revisionare e ti lasciamo un veicolo sostitutivo per continuare a lavorare senza interruzioni.
              A manutenzione finita, riportiamo il veicolo presso la tua sede.
            </p>
          </div>
        </div>
      </Section>

      <Section id="prenota">
        <BookingForm defaultSection="auto" />
      </Section>
    </PageLayout>
  );
}

function Card({ title, body, icon }) {
  return (
    <div className="card-flat p-6">
      {icon && (
        <div className="flex items-center justify-center h-11 w-11 mb-4 bg-gray-100 text-[color:var(--nsa-orange)]">
          {icon}
        </div>
      )}
      <h4 className="font-display uppercase text-xl">{title}</h4>
      <p className="mt-2 text-gray-600 leading-relaxed">{body}</p>
    </div>
  );
}
